import { Injectable } from '@angular/core';
import { AbstractControl, ValidatorFn, Validators } from '@angular/forms';
import { countries } from '../model/countries';
import { CurrencyModel, currencies } from '../model/currencies';
import { HttpClient, HttpEvent, HttpHeaders, HttpParams, HttpRequest } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { FilterData } from '../../warmi/accounts-receivable/model/filter-data.model';
import { InteractionStatus, InteractionStatusIds } from '../../warmi/accounts-receivable/model/interaction-status.model';
import { AdvisersAndSupervisorsResponse } from '../../warmi/accounts-receivable/model/userProjectWithDataResponse.model';
import { DiscountType } from '../../warmi/accounts-receivable/model/recoveryCampaign.model';
import { AccountSettings, BankAccountType, ValidationsStatus } from '../../warmi/my-account/model/account-settings.models';
import { AssignmentConfiguration, AssignmentPopulations, Intersection } from '../../warmi/strategies/model/assignmentConfiguration.model';
import { ClientGroupedByProject, ClientGroupedData } from '../../warmi/advisers/model/clientGroupedData';
import { AssignmentPopulation } from '../../warmi/advisers/model/AssignmentPopulation';
import { FieldManagementActionType } from '../../warmi/conversation/model/field-management.model';
import { CallManagementActionType } from '../../warmi/call/model/call-management.model';
import { CommunicationType, Interaction, InteractionPageable, InteractionStatusEnum } from '../../warmi/accounts-receivable/model/interaction.model';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { CountryCode } from '../../warmi/general/model/country.model';
import { PlanConfiguration } from '../../warmi/strategies/model/plan.model';
import { UserProjectPermission } from '../../warmi/my-account/model/user-project-permissions.model';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ChannelType } from '../../warmi/strategies/model/template.model';
import { VerificationAlert } from '../../warmi/credit/model/credit-verification-configuration.model';
import { PaymentBalanceType } from '../../warmi/my-account/model/my-project.models';
import { OnboardingProjectPermission } from '../../warmi/onboarding/model/onboardingStep';
import { AdditionalData, ClaimData, Client, ClientAlertSitutation, ClientRelationship, ClientSitutation, ContactabilityStatusIdentifier, CustomSituation } from '../../warmi/accounts-receivable/model/client.model';
import { MassiveProcess, MassiveProcessType } from '../../warmi/accounts-receivable/model/massiveProcess.model';
import { HierarchicalRelationEnum, ProjectType } from '../../warmi/my-account/model/project-settings.model';
import { EconomicSectorId } from '../../warmi/my-account/model/economic-sector.models';
import { ManagementActionsProjectCustom } from '../../warmi/my-account/model/management-actions-project.model';
import { PopulationCriteriaConfiguration } from '../../warmi/strategies/model/population.model';
import { AditionalVariableIdentifier, PopulationCriteriaIdentifier, PopulationCriteriaParam } from '../../warmi/strategies/model/populationCriteria.model'; 
import { DropdownFilter } from '../filter/dropdown-filter/dropdown-filter.component';
import { ContactabilityLevel } from '../../warmi/strategies/model/contactability.model';
import { ClaimStatus } from '../../warmi/accounts-receivable/model/claim.model';
import { DatePipe } from '@angular/common';

const INTERACTION_BACKEND_URI = `${environment.backendUri}/interaction`;
const API_INVOICE_URL = `${environment.backendUri}/invoice`;
const API_AUTH_TOKEN_KEY = `${environment.authTokenKey}`;

@Injectable({
  providedIn: 'root'
})
export class MoonflowUtilService {

  private helper = new JwtHelperService();
  private readonly _datePipe = new DatePipe('en-US');

  constructor(
    private http: HttpClient,
    private _translateService: TranslateService,
    ) { }


  formatBytes(bytes : number, decimals = 2) {
    if (!+bytes) return '0 Bytes'

    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ['kb', 'kb', 'mb', 'gb', 'TB', 'PB', 'EB', 'ZB', 'YB']

    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
  }

  getCurrencies() : CurrencyModel[] {
    let primaryCurrency = this.decryptedToken()?.primaryCurrency;
    let secondaryCurrency = this.decryptedToken()?.secondaryCurrency;
    let filteredCurrencies = currencies.filter(x => !x.showOnlyIfMatchesSecondaryCurrency || (x.showOnlyIfMatchesSecondaryCurrency && x.code == secondaryCurrency));
    filteredCurrencies.forEach(x =>{
      if(x.code == primaryCurrency) x.isPrimary = true;
      if(x.code == secondaryCurrency) x.isSecondary = true;
    })
    filteredCurrencies.sort((a, b) => {
        if (a.code === primaryCurrency) {
            return -1;
        } else if (a.code === secondaryCurrency && b.code !== primaryCurrency) {
            return 1;
        } else {
            return 0;
        }
    });
    return filteredCurrencies;

  }

  getSecondaryCurrency() : string {
    return this.decryptedToken()?.secondaryCurrency || 'USD';
  }

  getBusinessClientLevels() : { key : number; label : string}[] {
    return [
      {key : 1, label : 'COMMON.LEVEL.LEVEL_1'},
      {key : 2, label : 'COMMON.LEVEL.LEVEL_2'},
      {key : 3, label : 'COMMON.LEVEL.LEVEL_3'},
      {key : 4, label : 'COMMON.LEVEL.LEVEL_4'},
      {key : 5, label : 'COMMON.LEVEL.LEVEL_5'},
      {key : 6, label : 'COMMON.LEVEL.LEVEL_6'},
      {key : 7, label : 'COMMON.LEVEL.LEVEL_7'},
      {key : 8, label : 'COMMON.LEVEL.LEVEL_8'},
      {key : 9, label : 'COMMON.LEVEL.LEVEL_9'},
      {key : 10, label : 'COMMON.LEVEL.LEVEL_10'},
    ]
  }

  public getClientLevels() : { value : number; label : string; default?: boolean}[] {
    return [
      {value : 1, label : 'COMMON.LEVEL.LEVEL_1', default : true},
      {value : 2, label : 'COMMON.LEVEL.LEVEL_2'},
      {value : 3, label : 'COMMON.LEVEL.LEVEL_3'},
      {value : 4, label : 'COMMON.LEVEL.LEVEL_4'},
      {value : 5, label : 'COMMON.LEVEL.LEVEL_5'},
      {value : 6, label : 'COMMON.LEVEL.LEVEL_6'},
      {value : 7, label : 'COMMON.LEVEL.LEVEL_7'},
      {value : 8, label : 'COMMON.LEVEL.LEVEL_8'},
      {value : 9, label : 'COMMON.LEVEL.LEVEL_9'},
      {value : 10, label : 'COMMON.LEVEL.LEVEL_10'},
    ]
  }

  getChannelOptions(){
    return [
      { label: 'COMMON.CHANNEL_TYPE.WHATSAPP', value: ChannelType.WHATSAPP_CHANNEL_TYPE },
      { label: 'COMMON.CHANNEL_TYPE.EMAIL', value: ChannelType.EMAIL_CHANNEL_TYPE },
      { label: 'COMMON.CHANNEL_TYPE.SMS', value: ChannelType.SMS_CHANNEL_TYPE },
      { label: 'COMMON.CHANNEL_TYPE.PHONE_CALL', value: ChannelType.PHONE_CALL },
      { label: 'COMMON.CHANNEL_TYPE.FIELD_MANAGEMENT', value: ChannelType.FIELD_MANAGEMENT },
      { label: 'COMMON.CHANNEL_TYPE.WHATSAPP', value: ChannelType.WHATSAPP_CLOUD_API_CHANNEL_TYPE },
      { label: 'COMMON.CHANNEL_TYPE.EXTERNAL', value: ChannelType.EXTERNAL_CHANNEL_TYPE },
    ]
  }

  setPhoneNumberValidationsByPrefix(prefix : string,  control : AbstractControl, isRequired? : boolean){
    let countryId = null;
    if(prefix != null) {
      let country = countries.find(e => e.phone == prefix);
      if(country) countryId = Number(country.id);
    }
    this.setPhoneNumberValidationsByCountry(countryId, control, isRequired);
  }

  setPhoneNumberValidationsByCountry(countryId: number | null, control : AbstractControl, isRequired? : boolean) {
    let validators = [Validators.pattern(new RegExp(/^[0-9]+$/))];
    if(isRequired) validators.push(Validators.required);
    if(countryId != null){
      switch (countryId) {
        case 604:
          validators.push(Validators.minLength(9));
          validators.push(Validators.maxLength(9));
          break;
        case 484:
          validators.push(Validators.minLength(10));
          validators.push(Validators.maxLength(10));
          break;
        case 32:
          validators.push(Validators.minLength(10));
          validators.push(Validators.maxLength(11));
          break;
        case 591:
          validators.push(Validators.minLength(8));
          validators.push(Validators.maxLength(8));
            break;
        case 170:
          validators.push(Validators.minLength(8));
          validators.push(Validators.maxLength(10));
          break;
        default:
          validators.push(Validators.minLength(8));
          validators.push(Validators.maxLength(15));
          break;
      }
    }
    this.clearValidators(control);
    this.setValidators(control, Validators.compose(validators));
  }

  clearValidators(control: AbstractControl | null) {
    control?.clearValidators();
    control?.updateValueAndValidity();
  }

  setValidators(
    control: AbstractControl | null,
    validators: ValidatorFn | ValidatorFn[] | null
  ) {
    control?.setValidators(validators);
    control?.updateValueAndValidity();
  }

  getCurrencySymbol(currency : string | null | undefined){
    if(!currency) return '';
    else{
      let currencyData = currencies.find(x => x.code == currency);
      if(currencyData) return currencyData?.symbol;
    }
    return currency;
  }

  getList(page: any,clientId: string | null,startDate:string | null,endDate:string | null,
    communicationFlowIds: any,campaignIds: any,paymentActionIds: any,
    channelTypes: any,interactionStatusIds: any,filterSearch: any,supervisorProjectUserIds : any,
    adviserProjectUserIds : any,IsInteractionComponenet:boolean,portfolioTypes:any,validChannelActive:boolean,
    clientIds : any,actionType: any,processes : any, communicationTypes : any,destinationTypes : any, includeMissedOut : boolean, originTypes: any): Observable<InteractionPageable> {
    let param = new HttpParams();
    if (page != null) {param = param.append('page', page);}
    if (clientId != null && clientId.trim().length > 0) {param = param.append('clientId', clientId);}
    if (portfolioTypes != null && portfolioTypes.length > 0) {param = param.append('portfolioTypes', portfolioTypes);}
    if (startDate != null) {param = param.append('startDate', startDate);}
    if (endDate != null) {param = param.append('endDate', endDate);}
    if (communicationFlowIds != null && communicationFlowIds.length > 0) {param = param.append('communicationFlowIds', communicationFlowIds);}
    if (campaignIds != null && campaignIds.length > 0) {param = param.append('campaignIds', campaignIds);}
    if (processes != null && processes.length > 0) {param = param.append('processes', processes);}
    if (paymentActionIds != null && paymentActionIds.length > 0) {param = param.append('paymentActionIds', paymentActionIds);}
    if (channelTypes != null && channelTypes.length > 0) {
      param = param.append('channelTypes', channelTypes);
    }
    if (interactionStatusIds != null && interactionStatusIds.length > 0){param = param.append('interactionStatusIds', interactionStatusIds);}
    if (filterSearch != null && filterSearch.length > 0){param = param.append('search', filterSearch);}
    if (adviserProjectUserIds != null) {param = param.append('adviserProjectUserIds', adviserProjectUserIds);}
    if (supervisorProjectUserIds != null) {param = param.append('supervisorProjectUserIds', supervisorProjectUserIds);}
    if (validChannelActive != null) param = param.append('validChannelActive', validChannelActive);
    if (clientIds) param = param.append('clientIds', clientIds);
    if (actionType != null) param = param.append('actionType', actionType);
    if (communicationTypes != null && communicationTypes.length > 0) {param = param.append('communicationTypes', communicationTypes);}
    if (destinationTypes != null && destinationTypes.length > 0) {param = param.append('destinationTypes', destinationTypes);}
    if (includeMissedOut) param = param.append('includeMissedOut', includeMissedOut);
    if (originTypes) param = param.append('originTypes', originTypes);
    if(IsInteractionComponenet){
      return this.http.get<InteractionPageable>(`${INTERACTION_BACKEND_URI}/byfilter`,{ params: param });
    }else{
      return this.http.get<InteractionPageable>(`${API_INVOICE_URL}/interaction/byfilter`,{ params: param });
    }

  }

  getFilterData(IsInteractionComponenet:boolean) : Observable<{
    communicationFlows : FilterData[];
    campaigns : FilterData[];
    interactionStatuses : InteractionStatus[];
  }>{
    if(IsInteractionComponenet){
      return this.http.get<{
        communicationFlows : FilterData[];
        campaigns : FilterData[];
        interactionStatuses : InteractionStatus[];
      }>(`${INTERACTION_BACKEND_URI}/filters/all`);
    }else{
      return this.http.get<{
        communicationFlows : FilterData[];
        campaigns : FilterData[];
        interactionStatuses : InteractionStatus[];
      }>(`${API_INVOICE_URL}/interaction/filters/all`);
    }


  }

  getAdvisersAndSupervisors(IsInteractionComponenet:boolean): Observable<AdvisersAndSupervisorsResponse> {

    if(IsInteractionComponenet){
      return this.http.get<AdvisersAndSupervisorsResponse>(
        `${INTERACTION_BACKEND_URI}/advisersandsupervisors`
      );
    }else{
      return this.http.get<AdvisersAndSupervisorsResponse>(
        `${API_INVOICE_URL}/interaction/advisersandsupervisors`
      );
    }


  }

  getAccountTypes() : {value : string, label : string}[] {
    return [
      {label : "ACCOUNT_TYPE.SAVINGS", value : BankAccountType.SAVINGS_ACCOUNT},
      {label : "ACCOUNT_TYPE.CHECKING", value : BankAccountType.CHECKING_ACCOUNT},
    ]
  }

   upload(file: File, url: string): Observable<HttpEvent<any>> {
    let headers = new HttpHeaders();
    const formData: FormData = new FormData();
    formData.append('files', file);
    headers.append('Content-Type', file.type);
    const req = new HttpRequest('PUT', `${url}`, file, {
      headers: headers,
      reportProgress: true,
      responseType: 'json',
    });
    return this.http.request(req);
  }

  getRecoveryCampaignDiscountTypes(){
    return [
      {value : DiscountType.DISCOUNT_TYPE_PERCENTAGE, label : 'RECOVERY_CAMPAIGN_DISCOUNT_TYPE.PERCENTAGE'},
      {value : DiscountType.DISCOUNT_TYPE_AMOUNT, label : 'RECOVERY_CAMPAIGN_DISCOUNT_TYPE.AMOUNT'},
    ];
  }

  getVerificationStatus(accountSettingsBackup: AccountSettings){
    if(!accountSettingsBackup) return 'N';
    if(accountSettingsBackup?.validations?.legalInformationStatus == ValidationsStatus.SUCCESS_STATUS &&
      accountSettingsBackup?.validations?.taxInformationStatus == ValidationsStatus.SUCCESS_STATUS &&
      accountSettingsBackup?.validations?.bankInformationStatus == ValidationsStatus.SUCCESS_STATUS) return 'S';
      if(accountSettingsBackup.validations){
        if(accountSettingsBackup.validations.legalInformationStatus == ValidationsStatus.SUCCESS_STATUS &&
          accountSettingsBackup.validations.taxInformationStatus == ValidationsStatus.SUCCESS_STATUS &&
          accountSettingsBackup.validations.bankInformationStatus == ValidationsStatus.SUCCESS_STATUS) return 'S';
        else if(
          ((accountSettingsBackup.validations.legalInformationStatus == ValidationsStatus.PENDING_STATUS && accountSettingsBackup.legalConstitutionValidationFile?.length && accountSettingsBackup.legalRepresentativeValidationFile?.length) || accountSettingsBackup.validations.legalInformationStatus == ValidationsStatus.SUCCESS_STATUS) &&
          ((accountSettingsBackup.validations.taxInformationStatus == ValidationsStatus.PENDING_STATUS && accountSettingsBackup.taxCertificateValidationFile?.length && accountSettingsBackup.taxAddressCertificateValidationFile?.length) || accountSettingsBackup.validations.taxInformationStatus == ValidationsStatus.SUCCESS_STATUS) &&
          ((accountSettingsBackup.validations.bankInformationStatus == ValidationsStatus.PENDING_STATUS && accountSettingsBackup.bankInformation?.length) || accountSettingsBackup.validations.bankInformationStatus == ValidationsStatus.SUCCESS_STATUS)) return 'P';
      }
      return 'N';
  }

  getGroupedData(intersections : Intersection[], assignmentPopulation : AssignmentPopulations | AssignmentPopulation | null, clientGroupedData: ClientGroupedByProject, assignmentConfiguration : AssignmentConfiguration,  recoveryCampaignData? : boolean) : {amount : number, currency : string | null, percentage : number, amountPercentage : number, count : number, loading : boolean} | null  {

    let data : {amount : number, currency : string | null, percentage : number, amountPercentage : number; count : number, loading : boolean} = {amount : 0, currency : null, percentage : 0, amountPercentage : 0, count : 0, loading : true};
    if(!clientGroupedData || (!intersections && !recoveryCampaignData)) return null;
    let populationsGroupedDataFiltered : ClientGroupedData[] = [];
    if(!recoveryCampaignData){
      let populationIds = intersections?.map(x => x.populationId) || [];
      let populationsGroupedData = clientGroupedData.clients.filter(e => populationIds.includes(e.populationId));
      if(!populationsGroupedData?.length){
        data.currency = clientGroupedData.currency;
        data.loading = false;
        if(assignmentPopulation) assignmentPopulation.countData = data;
        return data;
      }
      intersections.map(x => {
        let section = assignmentConfiguration.sections.find(z => z.index == x.sectionIndex) || null;
        if(section){
          let filtered = populationsGroupedData.filter(y => y.populationId == x.populationId && (section?.from != null && section.from != undefined ?  y.days >= (section as any).from : true) && (section?.to != null && section.to != undefined ? y.days <= (section as any).to : true));
          populationsGroupedDataFiltered = populationsGroupedDataFiltered.concat(filtered);
        }
      })
    }
    else{
      if(clientGroupedData?.campaignClients?.length) populationsGroupedDataFiltered = [].concat(clientGroupedData.campaignClients as any);
      data.currency = clientGroupedData.currencyCampaign;
    }
    data = {amount : 0, currency : data.currency, percentage : 0, amountPercentage : 0, count : 0, loading : false};
    data.currency = populationsGroupedDataFiltered.find(e => e.currency)?.currency || null;
    data.amount = populationsGroupedDataFiltered.map(x => x.amount).reduce((a, b) => a + b, 0);
    data.count = populationsGroupedDataFiltered.map(x => x.count).reduce((a, b) => a + b, 0);
    if(clientGroupedData.clientsTotalCount) data.percentage = (data.count*100)/clientGroupedData.clientsTotalCount;
    if(recoveryCampaignData && clientGroupedData.totalAmountCampaign) data.amountPercentage = (data.amount*100)/clientGroupedData.totalAmountCampaign;
    else if(!recoveryCampaignData && clientGroupedData.totalAmount) data.amountPercentage = (data.amount*100)/clientGroupedData.totalAmount;
    if(assignmentPopulation) assignmentPopulation.countData = data;
    return data;
  }

  getStatus(status: number | string, interactionStatuses : InteractionStatus[], fillEmpty?: boolean) {
    switch (status) {
      case InteractionStatusEnum.INTERACTION_STATUS_SEND: return this._translateService.instant('INTERACTION_STATUS.SEND');
      case InteractionStatusEnum.INTERACTION_STATUS_DELIVERY: return this._translateService.instant('INTERACTION_STATUS.DELIVERY');
      case InteractionStatusEnum.INTERACTION_STATUS_OPEN: return this._translateService.instant('INTERACTION_STATUS.OPEN');
      case InteractionStatusEnum.INTERACTION_STATUS_GENERATED: return this._translateService.instant('INTERACTION_STATUS.GENERATED');
      case InteractionStatusEnum.INTERACTION_STATUS_PROGRAMMED: return this._translateService.instant('INTERACTION_STATUS.PROGRAMMED');
      case InteractionStatusEnum.INTERACTION_STATUS_FAILED: return this._translateService.instant('INTERACTION_STATUS.FAILED');
      case InteractionStatusEnum.INTERACTION_STATUS_CALL_MANAGMENT_CONTACT: return this._translateService.instant('INTERACTION_STATUS.CALL_MANAGMENT_CONTACT');
      case InteractionStatusEnum.INTERACTION_STATUS_CONTACT: return this._translateService.instant('INTERACTION_STATUS.CONTACT');
      case InteractionStatusEnum.INTERACTION_STATUS_CALL_MANAGMENT_NOT_CONTACT: return this._translateService.instant('INTERACTION_STATUS.CALL_MANAGMENT_NOT_CONTACT');
      case InteractionStatusEnum.INTERACTION_STATUS_NOT_CONTACT: return this._translateService.instant('INTERACTION_STATUS.NOT_CONTACT');
      case InteractionStatusEnum.INTERACTION_STATUS_CALL_MANAGMENT_TO_MANAGE: return this._translateService.instant('INTERACTION_STATUS.CALL_MANAGMENT_TO_MANAGE');
      case InteractionStatusEnum.INTERACTION_STATUS_MISS_OUT: return this._translateService.instant('INTERACTION_STATUS.MISS_OUT');
      case InteractionStatusEnum.INTERACTION_STATUS_SKIPPED: return this._translateService.instant('INTERACTION_STATUS.SKIPPED');
      case InteractionStatusEnum.INTERACTION_STATUS_CALL_MANAGEMENT_INBOUND_CONTACT: return !fillEmpty ? "" : this._translateService.instant('INTERACTION_STATUS.CALL_MANAGEMENT_INBOUND_CONTACT');
      case InteractionStatusEnum.INTERACTION_STATUS_INBOUND: return !fillEmpty ? "" : this._translateService.instant('INTERACTION_STATUS.INBOUND');
      case InteractionStatusEnum.INTERACTION_STATUS_IN_PROCESS: return this._translateService.instant('INTERACTION_STATUS.IN_PROCESS');
      case InteractionStatusEnum.INTERACTION_STATUS_NO_RESPONSE_FROM_PROVIDER: return this._translateService.instant('INTERACTION_STATUS.NO_RESPONSE_FROM_PROVIDER');
      case InteractionStatusEnum.INTERACTION_STATUS_CONTACT: return this._translateService.instant('INTERACTION_STATUS.CALL_MANAGMENT_CONTACT');
      case InteractionStatusEnum.INTERACTION_STATUS_NOT_CONTACT: return this._translateService.instant('INTERACTION_STATUS.CALL_MANAGMENT_NOT_CONTACT');
      case InteractionStatusEnum.INTERACTION_STATUS_INBOUND : return this._translateService.instant('INTERACTION_STATUS.CALL_MANAGEMENT_INBOUND_CONTACT');
      case InteractionStatusEnum.CALL_MANAGEMENT_SENT_TO_PROVIDER: return this._translateService.instant('INTERACTION_STATUS.CALL_MANAGEMENT_SENT_TO_PROVIDER');
      case InteractionStatusEnum.NOT_ANSWERED_BY_DESTINATION: return this._translateService.instant('INTERACTION_STATUS.NOT_ANSWERED_BY_DESTINATION');
      case InteractionStatusIds.INTERACTION_STATUS_AI_AGENT_PAUSED: return this._translateService.instant('INTERACTION_STATUS.AI_AGENT_PAUSED');
      case InteractionStatusIds.INTERACTION_STATUS_AI_AGENT_RESUMED: return this._translateService.instant('INTERACTION_STATUS.AI_AGENT_RESUMED');
      case InteractionStatusEnum.PHYSICAL_LETTER_READY: return this._translateService.instant('INTERACTION_STATUS.PHYSICAL_LETTER_READY');
      case InteractionStatusEnum.PHYSICAL_LETTER_PRINTING: return this._translateService.instant('INTERACTION_STATUS.PHYSICAL_LETTER_PRINTING');
      case InteractionStatusEnum.PHYSICAL_LETTER_IN_TRANSIT: return this._translateService.instant('INTERACTION_STATUS.PHYSICAL_LETTER_IN_TRANSIT');
      case InteractionStatusEnum.PHYSICAL_LETTER_OUT_FOR_DELIVERY: return this._translateService.instant('INTERACTION_STATUS.PHYSICAL_LETTER_OUT_FOR_DELIVERY');
      case InteractionStatusEnum.PHYSICAL_LETTER_DELIVERED: return this._translateService.instant('INTERACTION_STATUS.PHYSICAL_LETTER_DELIVERED');
      case InteractionStatusEnum.PHYSICAL_LETTER_RETURNED: return this._translateService.instant('INTERACTION_STATUS.PHYSICAL_LETTER_RETURNED');
      case InteractionStatusEnum.PHYSICAL_LETTER_CANCELLED: return this._translateService.instant('INTERACTION_STATUS.PHYSICAL_LETTER_CANCELLED');
    }
    let interactionStatus = interactionStatuses?.find(x => Number(x.id) == status);
    if (interactionStatus) return this._translateService.instant(interactionStatus.translationKey);
  }

  getInteractionStatusCallManagement(status: number | string){
    switch (status) {
      case InteractionStatusEnum.INTERACTION_STATUS_CALL_MANAGMENT_CONTACT:
      case InteractionStatusEnum.INTERACTION_STATUS_CALL_MANAGMENT_NOT_CONTACT:
      case InteractionStatusEnum.INTERACTION_STATUS_CONTACT:
      case InteractionStatusEnum.INTERACTION_STATUS_NOT_CONTACT:
      case InteractionStatusEnum.INTERACTION_STATUS_IN_PROCESS:
        return this._translateService.instant('CALL_MANAGEMENT.ACTIONS.OUTBOUND');
      case InteractionStatusEnum.INTERACTION_STATUS_CALL_MANAGEMENT_INBOUND_CONTACT:
      case InteractionStatusEnum.INTERACTION_STATUS_INBOUND:
        return this._translateService.instant('CALL_MANAGEMENT.ACTIONS.INBOUND');
    }
  }

  getInteractionStatusFieldManagement(status: number | string){
    switch (status) {
      case InteractionStatusEnum.INTERACTION_STATUS_CALL_MANAGMENT_CONTACT:
      case InteractionStatusEnum.INTERACTION_STATUS_CALL_MANAGMENT_NOT_CONTACT:
        case InteractionStatusEnum.INTERACTION_STATUS_CONTACT:
        return this._translateService.instant('CALL_MANAGEMENT.ACTIONS.OUTBOUND');
      case InteractionStatusEnum.INTERACTION_STATUS_CALL_MANAGEMENT_INBOUND_CONTACT:
        return this._translateService.instant('CALL_MANAGEMENT.ACTIONS.INBOUND');
    }
  }

  getStatusCallManagementSubStatus(status: string) {
    if (!status?.length) return '';
    switch (status) {
      case CallManagementActionType.PAYMENT_IN_REVIEW_OR_COMMITMENT_ACTION_TYPE:
      case CallManagementActionType.RETRY_CALL_ACTION_TYPE:
      case CallManagementActionType.REFUSE_TO_PAY_ACTION_TYPE:
      case CallManagementActionType.CANT_PAY_ACTION_TYPE:
      case CallManagementActionType.NO_RESPONSE_ACTION_TYPE:
      case CallManagementActionType.WRONG_PHONE_NUMBER_ACTION_TYPE:
      case CallManagementActionType.PAYMENT_RENEGOTIATION_TYPE:
      case CallManagementActionType.MESSAGE_IN_MAILBOX_TYPE:
      case CallManagementActionType.CONTACT_WITH_THIRD_PARTY_TYPE:
      case CallManagementActionType.REFINANCE_TYPE:
      case CallManagementActionType.PAYMENT_MADE_TYPE:
      case CallManagementActionType.PAYMENT_COMMITMENT_ACTION_TYPE:
      case CallManagementActionType.PAYMENT_IN_REVIEW_ACTION_TYPE:
      case CallManagementActionType.COMMUNICATION_REMINDER_TYPE:
      case CallManagementActionType.PAUSED_COMMUNICATION_NO_CONTACT_ACTION_TYPE:
      case CallManagementActionType.PAUSED_COMMUNICATION_CONTACT_ACTION_TYPE:
      case CallManagementActionType.CONTACT_HUNG_UP_ACTION_TYPE:
      case CallManagementActionType.CALL_SCHEDULED_ACTION_TYPE:
      case CallManagementActionType.NO_CONTACTED_ACTION_TYPE:
      case CallManagementActionType.NO_CONVERSATION_ACTION_TYPE:
        return "- " + this.getStatusCallManagementSubStatusOnlyDescription(status);
      default:
        return this.getStatusCallManagementSubStatusOnlyDescription(status);
    }
  }

  getStatusCallManagementSubStatusOnlyDescription(status: string) {
    if (!status?.length) return '';
    switch (status) {
      case CallManagementActionType.PAYMENT_IN_REVIEW_OR_COMMITMENT_ACTION_TYPE: return  this._translateService.instant('CALL_MANAGEMENT.ACTIONS.COMMITMENT_VERIFY');
      case CallManagementActionType.INBOUND_PAYMENT_IN_REVIEW_OR_COMMITMENT_ACTION_TYPE: return this._translateService.instant('CALL_MANAGEMENT.ACTIONS.COMMITMENT_VERIFY');
      case CallManagementActionType.RETRY_CALL_ACTION_TYPE: return  this._translateService.instant('CALL_MANAGEMENT.ACTIONS.RETRY_CALL');
      case CallManagementActionType.INBOUND_RETRY_CALL_ACTION_TYPE: return this._translateService.instant('CALL_MANAGEMENT.ACTIONS.RETRY_CALL');
      case CallManagementActionType.REFUSE_TO_PAY_ACTION_TYPE: return  this._translateService.instant('CALL_MANAGEMENT.ACTIONS.REFUSE_TO_PAY');
      case CallManagementActionType.CANT_PAY_ACTION_TYPE: return  this._translateService.instant('CALL_MANAGEMENT.ACTIONS.CANT_PAY');
      case CallManagementActionType.NO_RESPONSE_ACTION_TYPE: return  this._translateService.instant('CALL_MANAGEMENT.ACTIONS.NO_RESPONSE');
      case CallManagementActionType.WRONG_PHONE_NUMBER_ACTION_TYPE: return  this._translateService.instant('CALL_MANAGEMENT.ACTIONS.WRONG_NUMBER');
      case CallManagementActionType.PAYMENT_RENEGOTIATION_TYPE: return  this._translateService.instant('CALL_MANAGEMENT.ACTIONS.PAYMENT_RENEGOTIATION');
      case CallManagementActionType.INBOUND_PAYMENT_RENEGOTIATION_TYPE: return this._translateService.instant('CALL_MANAGEMENT.ACTIONS.PAYMENT_RENEGOTIATION');
      case CallManagementActionType.MESSAGE_IN_MAILBOX_TYPE: return  this._translateService.instant('CALL_MANAGEMENT.ACTIONS.MESSAGE_IN_MAILBOX');
      case CallManagementActionType.CONTACT_WITH_THIRD_PARTY_TYPE: return  this._translateService.instant('CALL_MANAGEMENT.ACTIONS.CONTACT_WITH_THIRD_PARTY');
      case CallManagementActionType.INBOUND_REQUESTED_INFORMATION_TYPE: return this._translateService.instant('CALL_MANAGEMENT.ACTIONS.REQUESTED_INFORMATION');
      case CallManagementActionType.INBOUND_CLAIM_TYPE: return this._translateService.instant('CALL_MANAGEMENT.ACTIONS.CLAIM');
      case CallManagementActionType.REFINANCE_TYPE: return  this._translateService.instant('CALL_MANAGEMENT.ACTIONS.REFINANCE');
      case CallManagementActionType.INBOUND_REFINANCE_TYPE: return this._translateService.instant('CALL_MANAGEMENT.ACTIONS.REFINANCE');
      case CallManagementActionType.PAYMENT_MADE_TYPE: return  this._translateService.instant('CALL_MANAGEMENT.ACTIONS.PAYMENT_MADE');
      case CallManagementActionType.INBOUND_PAYMENT_MADE_TYPE: return this._translateService.instant('CALL_MANAGEMENT.ACTIONS.PAYMENT_MADE');
      case CallManagementActionType.PAYMENT_COMMITMENT_ACTION_TYPE: return  this._translateService.instant('CALL_MANAGEMENT.ACTIONS.PAYMENT_COMMITMENT');
      case CallManagementActionType.INBOUND_PAYMENT_COMMITMENT_ACTION_TYPE: return this._translateService.instant('CALL_MANAGEMENT.ACTIONS.PAYMENT_COMMITMENT');
      case CallManagementActionType.PAYMENT_IN_REVIEW_ACTION_TYPE: return  this._translateService.instant('CALL_MANAGEMENT.ACTIONS.PAYMENT_IN_REVIEW');
      case CallManagementActionType.INBOUND_PAYMENT_IN_REVIEW_ACTION_TYPE: return this._translateService.instant('CALL_MANAGEMENT.ACTIONS.PAYMENT_IN_REVIEW');
      case CallManagementActionType.IN_PROCESS_TYPE: return "";
      case CallManagementActionType.COMMUNICATION_REMINDER_TYPE: return  this._translateService.instant('CALL_MANAGEMENT.ACTIONS.COMMUNICATION_REMINDER');
      case CallManagementActionType.PAUSED_COMMUNICATION_CONTACT_ACTION_TYPE:
      case CallManagementActionType.PAUSED_COMMUNICATION_NO_CONTACT_ACTION_TYPE:
      case CallManagementActionType.PAUSED_COMMUNICATION_INBOUND_ACTION_TYPE:
        return  this._translateService.instant('CALL_MANAGEMENT.ACTIONS.PAUSED_UNTIL');
      case CallManagementActionType.CONTACT_HUNG_UP_ACTION_TYPE:
      case CallManagementActionType.INBOUND_CONTACT_HUNG_UP_ACTION_TYPE:
        return this._translateService.instant('CALL_MANAGEMENT.ACTIONS.CONTACT_HUNG_UP');
      case CallManagementActionType.CALL_SCHEDULED_ACTION_TYPE: return this._translateService.instant('CALL_MANAGEMENT.ACTIONS.CALL_SCHEDULED_ACTION_TYPE');
      case CallManagementActionType.NO_CONTACTED_ACTION_TYPE: return this._translateService.instant('CALL_MANAGEMENT.ACTIONS.NO_CONTACTED');
      case CallManagementActionType.NO_CONVERSATION_ACTION_TYPE: return this._translateService.instant('CALL_MANAGEMENT.ACTIONS.NO_CONVERSATION');
    }
    return '';
  }

  getStatusFieldManagementSubStatus(status: string) {
    if (!status?.length) return '';
    switch (status) {
      case FieldManagementActionType.PAYMENT_IN_REVIEW_OR_COMMITMENT_ACTION_TYPE:
      case FieldManagementActionType.RETRY_CALL_ACTION_TYPE:
      case FieldManagementActionType.REFUSE_TO_PAY_ACTION_TYPE:
      case FieldManagementActionType.CANT_PAY_ACTION_TYPE:
      case FieldManagementActionType.NO_ANSWER_ACTION_TYPE:
      case FieldManagementActionType.WRONG_ADDRESS_ACTION_TYPE:
      case FieldManagementActionType.PAYMENT_RENEGOTIATION_TYPE:
      case FieldManagementActionType.MESSAGE_IN_MAILBOX_TYPE:
      case FieldManagementActionType.CONTACT_WITH_THIRD_PARTY_TYPE:
      case FieldManagementActionType.REFINANCE_TYPE:
      case FieldManagementActionType.RETRY_MANAGEMENT_ACTION_TYPE:
      case FieldManagementActionType.UPDATE_DATA_ACTION_TYPE:
        return "- " + this.getStatusFieldManagementSubStatusOnlyDescription(status);
      default:
        return this.getStatusFieldManagementSubStatusOnlyDescription(status);
    }
    return '';
  }

  getStatusFieldManagementSubStatusOnlyDescription(status: string) {
    if (!status?.length) return '';
    switch (status) {
      case FieldManagementActionType.PAYMENT_IN_REVIEW_OR_COMMITMENT_ACTION_TYPE: return this._translateService.instant('FIELD_MANAGEMENT.ACTIONS.COMMITMENT_VERIFY');
      case FieldManagementActionType.PAYMENT_COMMITMENT_ACTION_TYPE: return this._translateService.instant('FIELD_MANAGEMENT.ACTIONS.PAYMENT_COMMITMENT');
      case FieldManagementActionType.PAYMENT_IN_REVIEW_ACTION_TYPE: return this._translateService.instant('FIELD_MANAGEMENT.ACTIONS.PAYMENT_IN_REVIEW');
      case FieldManagementActionType.INBOUND_PAYMENT_IN_REVIEW_OR_COMMITMENT_ACTION_TYPE: return this._translateService.instant('FIELD_MANAGEMENT.ACTIONS.COMMITMENT_VERIFY');
      case FieldManagementActionType.RETRY_CALL_ACTION_TYPE: return this._translateService.instant('FIELD_MANAGEMENT.ACTIONS.RETRY_CALL');
      case FieldManagementActionType.REFUSE_TO_PAY_ACTION_TYPE: return this._translateService.instant('FIELD_MANAGEMENT.ACTIONS.REFUSE_TO_PAY');
      case FieldManagementActionType.CANT_PAY_ACTION_TYPE: return this._translateService.instant('FIELD_MANAGEMENT.ACTIONS.CANT_PAY');
      case FieldManagementActionType.NO_ANSWER_ACTION_TYPE: return this._translateService.instant('FIELD_MANAGEMENT.ACTIONS.NO_ANSWER');
      case FieldManagementActionType.WRONG_ADDRESS_ACTION_TYPE: return this._translateService.instant('FIELD_MANAGEMENT.ACTIONS.WRONG_ADDRESS');
      case FieldManagementActionType.PAYMENT_RENEGOTIATION_TYPE: return this._translateService.instant('FIELD_MANAGEMENT.ACTIONS.PAYMENT_RENEGOTIATION');
      case FieldManagementActionType.INBOUND_PAYMENT_RENEGOTIATION_TYPE: return this._translateService.instant('FIELD_MANAGEMENT.ACTIONS.PAYMENT_RENEGOTIATION');
      case FieldManagementActionType.MESSAGE_IN_MAILBOX_TYPE: return this._translateService.instant('FIELD_MANAGEMENT.ACTIONS.MESSAGE_IN_MAILBOX');
      case FieldManagementActionType.CONTACT_WITH_THIRD_PARTY_TYPE: return this._translateService.instant('FIELD_MANAGEMENT.ACTIONS.CONTACT_WITH_THIRD_PARTY');
      case FieldManagementActionType.INBOUND_REQUESTED_INFORMATION_TYPE: return this._translateService.instant('FIELD_MANAGEMENT.ACTIONS.REQUESTED_INFORMATION');
      case FieldManagementActionType.INBOUND_CLAIM_TYPE: return this._translateService.instant('FIELD_MANAGEMENT.ACTIONS.CLAIM');
      case FieldManagementActionType.REFINANCE_TYPE: return this._translateService.instant('FIELD_MANAGEMENT.ACTIONS.REFINANCE');
      case FieldManagementActionType.INBOUND_REFINANCE_TYPE: return this._translateService.instant('FIELD_MANAGEMENT.ACTIONS.REFINANCE');
      case FieldManagementActionType.RETRY_MANAGEMENT_ACTION_TYPE: return this._translateService.instant('FIELD_MANAGEMENT.ACTIONS.RETRY_MANAGEMENT');
      case FieldManagementActionType.UPDATE_DATA_ACTION_TYPE: return this._translateService.instant('FIELD_MANAGEMENT.ACTIONS.UPDATE_DATA');
      case FieldManagementActionType.INBOUND_PAYMENT_COMMITMENT_ACTION_TYPE: return this._translateService.instant('FIELD_MANAGEMENT.ACTIONS.PAYMENT_COMMITMENT');
      case FieldManagementActionType.INBOUND_PAYMENT_IN_REVIEW_ACTION_TYPE: return this._translateService.instant('FIELD_MANAGEMENT.ACTIONS.PAYMENT_IN_REVIEW');
      case FieldManagementActionType.PAYMENT_MADE_TYPE: return this._translateService.instant('FIELD_MANAGEMENT.ACTIONS.PAYMENT_MADE');
      case FieldManagementActionType.INBOUND_PAYMENT_MADE_TYPE: return this._translateService.instant('FIELD_MANAGEMENT.ACTIONS.PAYMENT_MADE');
    }
    return '';
  }

  getFieldManagementVisitResultFilterLabel(actionType: string): string {
    if (!actionType?.length) {
      return '';
    }

    let label = this.getStatusFieldManagementSubStatusOnlyDescription(actionType);
    if (!label?.length) {
      const actionKey = `FIELD_MANAGEMENT.ACTIONS.${actionType.replace(/([A-Z])/g, '_$1').toUpperCase()}`;
      const translated = this._translateService.instant(actionKey);
      label = translated !== actionKey ? translated : actionType;
    }

    const inboundActionTypes = new Set<string>([
      FieldManagementActionType.INBOUND_PAYMENT_IN_REVIEW_OR_COMMITMENT_ACTION_TYPE,
      FieldManagementActionType.INBOUND_PAYMENT_RENEGOTIATION_TYPE,
      FieldManagementActionType.INBOUND_REQUESTED_INFORMATION_TYPE,
      FieldManagementActionType.INBOUND_CLAIM_TYPE,
      FieldManagementActionType.INBOUND_REFINANCE_TYPE,
      FieldManagementActionType.INBOUND_PAYMENT_COMMITMENT_ACTION_TYPE,
      FieldManagementActionType.INBOUND_PAYMENT_IN_REVIEW_ACTION_TYPE,
      FieldManagementActionType.INBOUND_PAYMENT_MADE_TYPE,
    ]);

    const outboundPairedActionTypes = new Set<string>([
      FieldManagementActionType.PAYMENT_IN_REVIEW_OR_COMMITMENT_ACTION_TYPE,
      FieldManagementActionType.PAYMENT_COMMITMENT_ACTION_TYPE,
      FieldManagementActionType.PAYMENT_IN_REVIEW_ACTION_TYPE,
      FieldManagementActionType.PAYMENT_RENEGOTIATION_TYPE,
      FieldManagementActionType.REFINANCE_TYPE,
      FieldManagementActionType.PAYMENT_MADE_TYPE,
    ]);

    if (inboundActionTypes.has(actionType)) {
      return `${this._translateService.instant('FIELD_MANAGEMENT.ACTIONS.INBOUND')}: ${label}`;
    }
    if (outboundPairedActionTypes.has(actionType)) {
      return `${this._translateService.instant('FIELD_MANAGEMENT.ACTIONS.OUTBOUND')}: ${label}`;
    }
    return label;
  }


  getAddionalFilterForInteractions(interactionStatus : InteractionStatus[], catalog: ManagementActionsProjectCustom[]){

    const getActionName = (actionType: string, fallbackKey: string): string => {
      const actionKey = this.constantToKeyMapCallManagementActions[actionType];
      const catalogItem = catalog?.find(item => item.actionKey === actionKey);

      // Si existe en el catálogo, devolvemos customName (que ya trae la llave si no fue editado)
      // de lo contrario, devolvemos la llave estática que ya tenías.
      const nameToTranslate = catalogItem ? catalogItem.customName : fallbackKey;
      return this._translateService.instant(nameToTranslate);
    };

    const outboundLabel = this._translateService.instant('CALL_MANAGEMENT.ACTIONS.OUTBOUND');
    const inboundLabel = this._translateService.instant('CALL_MANAGEMENT.ACTIONS.INBOUND');
    const contactLabel = this._translateService.instant(this.getStatus(InteractionStatusIds.INTERACTION_STATUS_CALL_MANAGMENT_CONTACT.toString(), interactionStatus));
    const noContactLabel = this._translateService.instant(this.getStatus(InteractionStatusIds.INTERACTION_STATUS_CALL_MANAGMENT_NOT_CONTACT.toString(), interactionStatus));

    return [
      // --- OUTBOUND CONTACTED ---
      {
        label: `${outboundLabel}: ${contactLabel} ${getActionName(CallManagementActionType.PAYMENT_IN_REVIEW_OR_COMMITMENT_ACTION_TYPE, 'CALL_MANAGEMENT.ACTIONS.COMMITMENT_VERIFY')}`,
        value: InteractionStatusIds.INTERACTION_STATUS_CALL_MANAGMENT_CONTACT,
        additionalValue: CallManagementActionType.PAYMENT_IN_REVIEW_OR_COMMITMENT_ACTION_TYPE
      },
      {
        label: `${outboundLabel}: ${contactLabel} ${getActionName(CallManagementActionType.RETRY_CALL_ACTION_TYPE, 'CALL_MANAGEMENT.ACTIONS.RETRY_CALL')}`,
        value: InteractionStatusIds.INTERACTION_STATUS_CALL_MANAGMENT_CONTACT,
        additionalValue: CallManagementActionType.RETRY_CALL_ACTION_TYPE
      },
      {
        label: `${outboundLabel}: ${contactLabel} ${getActionName(CallManagementActionType.REFUSE_TO_PAY_ACTION_TYPE, 'CALL_MANAGEMENT.ACTIONS.REFUSE_TO_PAY')}`,
        value: InteractionStatusIds.INTERACTION_STATUS_CALL_MANAGMENT_CONTACT,
        additionalValue: CallManagementActionType.REFUSE_TO_PAY_ACTION_TYPE
      },
      {
        label: `${outboundLabel}: ${contactLabel} ${getActionName(CallManagementActionType.CANT_PAY_ACTION_TYPE, 'CALL_MANAGEMENT.ACTIONS.CANT_PAY')}`,
        value: InteractionStatusIds.INTERACTION_STATUS_CALL_MANAGMENT_CONTACT,
        additionalValue: CallManagementActionType.CANT_PAY_ACTION_TYPE
      },
      {
        label: `${outboundLabel}: ${contactLabel} ${getActionName(CallManagementActionType.PAYMENT_RENEGOTIATION_TYPE, 'CALL_MANAGEMENT.ACTIONS.PAYMENT_RENEGOTIATION')}`,
        value: InteractionStatusIds.INTERACTION_STATUS_CALL_MANAGMENT_CONTACT,
        additionalValue: CallManagementActionType.PAYMENT_RENEGOTIATION_TYPE
      },
      {
        label: `${outboundLabel}: ${contactLabel} ${getActionName(CallManagementActionType.REFINANCE_TYPE, 'CALL_MANAGEMENT.ACTIONS.REFINANCE')}`,
        value: InteractionStatusIds.INTERACTION_STATUS_CALL_MANAGMENT_CONTACT,
        additionalValue: CallManagementActionType.REFINANCE_TYPE
      },
      {
        label: `${outboundLabel}: ${contactLabel} ${getActionName(CallManagementActionType.PAYMENT_MADE_TYPE, 'CALL_MANAGEMENT.ACTIONS.PAYMENT_MADE')}`,
        value: InteractionStatusIds.INTERACTION_STATUS_CALL_MANAGMENT_CONTACT,
        additionalValue: CallManagementActionType.PAYMENT_MADE_TYPE
      },
      {
        label: `${outboundLabel}: ${contactLabel} ${getActionName(CallManagementActionType.COMMUNICATION_REMINDER_TYPE, 'CALL_MANAGEMENT.ACTIONS.COMMUNICATION_REMINDER')}`,
        value: InteractionStatusIds.INTERACTION_STATUS_CALL_MANAGMENT_CONTACT,
        additionalValue: CallManagementActionType.COMMUNICATION_REMINDER_TYPE
      },
      {
        label: `${outboundLabel}: ${contactLabel} - ${this._translateService.instant("CALL_MANAGEMENT.ACTIONS.PAUSED_UNTIL_DESCRIPTION")}`,
        value: InteractionStatusIds.INTERACTION_STATUS_CALL_MANAGMENT_CONTACT,
        additionalValue: CallManagementActionType.PAUSED_COMMUNICATION_CONTACT_ACTION_TYPE
      },
      {
        label: `${outboundLabel}: ${contactLabel} ${getActionName(CallManagementActionType.CONTACT_HUNG_UP_ACTION_TYPE, 'CALL_MANAGEMENT.ACTIONS.CONTACT_HUNG_UP')}`,
        value: InteractionStatusIds.INTERACTION_STATUS_CALL_MANAGMENT_CONTACT,
        additionalValue: CallManagementActionType.CONTACT_HUNG_UP_ACTION_TYPE
      },

      // --- OUTBOUND NO CONTACT ---
      {
        label: `${outboundLabel}: ${noContactLabel} ${getActionName(CallManagementActionType.NO_RESPONSE_ACTION_TYPE, 'CALL_MANAGEMENT.ACTIONS.NO_RESPONSE')}`,
        value: InteractionStatusIds.INTERACTION_STATUS_CALL_MANAGMENT_NOT_CONTACT,
        additionalValue: CallManagementActionType.NO_RESPONSE_ACTION_TYPE
      },
      {
        label: `${outboundLabel}: ${noContactLabel} ${getActionName(CallManagementActionType.WRONG_PHONE_NUMBER_ACTION_TYPE, 'CALL_MANAGEMENT.ACTIONS.WRONG_NUMBER')}`,
        value: InteractionStatusIds.INTERACTION_STATUS_CALL_MANAGMENT_NOT_CONTACT,
        additionalValue: CallManagementActionType.WRONG_PHONE_NUMBER_ACTION_TYPE
      },
      {
        label: `${outboundLabel}: ${noContactLabel} ${getActionName(CallManagementActionType.MESSAGE_IN_MAILBOX_TYPE, 'CALL_MANAGEMENT.ACTIONS.MESSAGE_IN_MAILBOX')}`,
        value: InteractionStatusIds.INTERACTION_STATUS_CALL_MANAGMENT_NOT_CONTACT,
        additionalValue: CallManagementActionType.MESSAGE_IN_MAILBOX_TYPE
      },
      {
        label: `${outboundLabel}: ${noContactLabel} ${getActionName(CallManagementActionType.CONTACT_WITH_THIRD_PARTY_TYPE, 'CALL_MANAGEMENT.ACTIONS.CONTACT_WITH_THIRD_PARTY')}`,
        value: InteractionStatusIds.INTERACTION_STATUS_CALL_MANAGMENT_NOT_CONTACT,
        additionalValue: CallManagementActionType.CONTACT_WITH_THIRD_PARTY_TYPE
      },
      {
        label: `${outboundLabel}: ${noContactLabel} - ${this._translateService.instant("CALL_MANAGEMENT.ACTIONS.PAUSED_UNTIL_DESCRIPTION")}`,
        value: InteractionStatusIds.INTERACTION_STATUS_CALL_MANAGMENT_NOT_CONTACT,
        additionalValue: CallManagementActionType.PAUSED_COMMUNICATION_NO_CONTACT_ACTION_TYPE
      },

      // --- INBOUND ---
      {
        label: `${inboundLabel}: ${getActionName(CallManagementActionType.INBOUND_PAYMENT_IN_REVIEW_OR_COMMITMENT_ACTION_TYPE, 'CALL_MANAGEMENT.ACTIONS.COMMITMENT_VERIFY')}`,
        value: InteractionStatusIds.INTERACTION_STATUS_CALL_MANAGMENT_CONTACT,
        additionalValue: CallManagementActionType.INBOUND_PAYMENT_IN_REVIEW_OR_COMMITMENT_ACTION_TYPE
      },
      {
        label: `${inboundLabel}: ${getActionName(CallManagementActionType.INBOUND_PAYMENT_RENEGOTIATION_TYPE, 'CALL_MANAGEMENT.ACTIONS.PAYMENT_RENEGOTIATION')}`,
        value: InteractionStatusIds.INTERACTION_STATUS_CALL_MANAGMENT_CONTACT,
        additionalValue: CallManagementActionType.INBOUND_PAYMENT_RENEGOTIATION_TYPE
      },
      {
        label: `${inboundLabel}: ${getActionName(CallManagementActionType.INBOUND_REFINANCE_TYPE, 'CALL_MANAGEMENT.ACTIONS.REFINANCE')}`,
        value: InteractionStatusIds.INTERACTION_STATUS_CALL_MANAGMENT_CONTACT,
        additionalValue: CallManagementActionType.INBOUND_REFINANCE_TYPE
      },
      {
        label: `${inboundLabel}: ${getActionName(CallManagementActionType.INBOUND_REQUESTED_INFORMATION_TYPE, 'CALL_MANAGEMENT.ACTIONS.REQUESTED_INFORMATION')}`,
        value: InteractionStatusIds.INTERACTION_STATUS_CALL_MANAGMENT_CONTACT,
        additionalValue: CallManagementActionType.INBOUND_REQUESTED_INFORMATION_TYPE
      },
      {
        label: `${inboundLabel}: ${getActionName(CallManagementActionType.INBOUND_CLAIM_TYPE, 'CALL_MANAGEMENT.ACTIONS.CLAIM')}`,
        value: InteractionStatusIds.INTERACTION_STATUS_CALL_MANAGMENT_CONTACT,
        additionalValue: CallManagementActionType.INBOUND_CLAIM_TYPE
      },
      {
        label: `${inboundLabel}: ${getActionName(CallManagementActionType.INBOUND_PAYMENT_MADE_TYPE, 'CALL_MANAGEMENT.ACTIONS.PAYMENT_MADE')}`,
        value: InteractionStatusIds.INTERACTION_STATUS_CALL_MANAGMENT_CONTACT,
        additionalValue: CallManagementActionType.INBOUND_PAYMENT_MADE_TYPE
      },
      {
        label: `${inboundLabel}: ${getActionName(CallManagementActionType.INBOUND_RETRY_CALL_ACTION_TYPE, 'CALL_MANAGEMENT.ACTIONS.RETRY_CALL')}`,
        value: InteractionStatusIds.INTERACTION_STATUS_CALL_MANAGMENT_CONTACT,
        additionalValue: CallManagementActionType.INBOUND_RETRY_CALL_ACTION_TYPE
      },
      {
        label: `${inboundLabel}: ${getActionName(CallManagementActionType.INBOUND_CONTACT_HUNG_UP_ACTION_TYPE, 'CALL_MANAGEMENT.ACTIONS.CONTACT_HUNG_UP')}`,
        value: InteractionStatusIds.INTERACTION_STATUS_CALL_MANAGMENT_CONTACT,
        additionalValue: CallManagementActionType.INBOUND_CONTACT_HUNG_UP_ACTION_TYPE
      },
      {
        label: `${inboundLabel}: ${this._translateService.instant("CALL_MANAGEMENT.ACTIONS.PAUSED_UNTIL_DESCRIPTION")}`,
        value: InteractionStatusIds.INTERACTION_STATUS_CALL_MANAGMENT_CONTACT,
        additionalValue: CallManagementActionType.PAUSED_COMMUNICATION_INBOUND_ACTION_TYPE
      }
    ];
  }

  isPausedLastActionType(status: string){
    return status == CallManagementActionType.PAUSED_COMMUNICATION_CONTACT_ACTION_TYPE
      || status == CallManagementActionType.PAUSED_COMMUNICATION_NO_CONTACT_ACTION_TYPE
      || status == CallManagementActionType.PAUSED_COMMUNICATION_INBOUND_ACTION_TYPE
  }

  isInboundManagementActionType(status: string){
    if (!status?.length) return false;
    return status.startsWith('inbound')
      || status == CallManagementActionType.PAUSED_COMMUNICATION_INBOUND_ACTION_TYPE;
  }

  convertPhoneNumberToCountry(countryCode: string, phoneNumber: string | null, prefix: string | null): string | null {
    if (phoneNumber == null || countryCode == null) return phoneNumber;
    if (phoneNumber.startsWith("+")) return phoneNumber;
    if(prefix == null){
      switch (countryCode) {
        case CountryCode.ARGENTINA_COUNTRY_CODE:
          prefix = "+54";
          break;
        case CountryCode.MEXICO_COUNTRY_CODE:
          prefix = "+52";
          break;
        case CountryCode.PERU_COUNTRY_CODE:
          prefix = "+51";
          break;
        case CountryCode.PANAMA_COUNTRY_CODE:
          prefix = "+507";
          break;
      }
    }
    if (prefix == null && countryCode != null) {
      let countriesData = countries.find(e => e.id == countryCode);
      prefix = countriesData?.phone || null;
    }
    if (prefix != null) {
      if (!prefix.startsWith("+")) prefix = "+" + prefix;
      if (!phoneNumber.startsWith(prefix)) phoneNumber = prefix + phoneNumber;
    }
    return phoneNumber;
  }

  convertSecondsToFormat(conversationSeconds : number | null) : string{
    return moment.utc((conversationSeconds || 0)*1000).format('HH:mm:ss');
  }


  public getLanguage() : { value : string; label : string}[] {
    return [
      {value: 'es', label: 'LANGUAGE.SPANISH'},
      {value: 'en', label: 'LANGUAGE.ENGLISH'},
      {value: 'pt-BR', label: 'LANGUAGE.PORTUGUESE_BR'}
    ]
  }

  getBooleanValueFromJsonFilteredByKeysWithDots(data : {[x : string] : any}, key : string) : boolean {
    if(!key || !key.length) return false;
    let keyList = key.split(".");
    let keyBase = keyList[0]+"";
    if(!data?.hasOwnProperty(keyBase)) return false;
    if(keyList.length == 1){
      if(typeof data[keyBase] == "boolean") return data[keyBase];
      else return false;
    }
    keyList.splice(0, 1);
    return this.getBooleanValueFromJsonFilteredByKeysWithDots(data[keyBase], keyList.join("."));
  }

  canAccessPlanBenefit(keys : string[], configuration : PlanConfiguration){
    if(!configuration) return false;
    for (const key of keys) {
      if(!this.getBooleanValueFromJsonFilteredByKeysWithDots(configuration as any, key)) return false;
    }
    return true;
  }

  decryptedToken(): {
    sub: string;
    roleId: any;
    permissions : UserProjectPermission[] | null;
    projectId?: string | null;
    canCall : boolean;
    userId : string;
    userProjectId?: number | null;
    projectType : string;
    timeZone?: string | null;
    countryId: string;
    economicSectorId : number | null;
    chargeEntity : string | null;
    primaryCurrency : string | null;
    secondaryCurrency : string | null;
    pageComponentsConfiguration : string | null;
    planConfiguration : PlanConfiguration | null;
    allowIndicators : boolean | false;
    callProvider : string | null;
    isSuperadmin : boolean | false;
    owner : boolean;
    paymentBalanceType : string | null;
    onboardingPermission : OnboardingProjectPermission[] | null;
    onboardingRoleId : number;
    callExtension : string | null;
    unappliedPaymentsAndCustomerAdvances : boolean;
    hierarchicalRelation : string | null;
    allowNegativeForUnappliedPaymentsAndCustomerAdvances : boolean;
  } | null {
    const token = localStorage.getItem(API_AUTH_TOKEN_KEY);
    if (token === 'undefined' || !token) {
      localStorage.removeItem(API_AUTH_TOKEN_KEY);
      return null;
    }
    let decryptedToken = this.helper.decodeToken(token);
    const activeProjectId = this.getActiveProjectId();
    if(decryptedToken?.permissions){
      try{
        decryptedToken.permissions = JSON.parse(decryptedToken.permissions)
      }catch(err){
        if(environment.production) console.error(err);
      }
    }
    if(decryptedToken?.onboardingPermission){
      try{
        decryptedToken.onboardingPermission = JSON.parse(decryptedToken.onboardingPermission);
      }catch(err){
        if(environment.production) console.error(err);
      }
    }
    if(decryptedToken?.planConfiguration){
      try{
        decryptedToken.planConfiguration = JSON.parse(decryptedToken.planConfiguration)
      }catch(err){
        if(environment.production) console.error(err);
      }
    }
    if ((!activeProjectId && decryptedToken?.projectId) || (activeProjectId && decryptedToken?.projectId && activeProjectId != decryptedToken?.projectId))
      sessionStorage.setItem(
        environment.activeProjectCookieKey,
        decryptedToken?.projectId
      );
    return decryptedToken;
  }

  getActiveProjectId(cookieKey?: string) {
    const projectId = sessionStorage.getItem(
      cookieKey || environment.activeProjectCookieKey
    );
    return projectId;
  }

  getListCallManagmentActions(){
   return [
      { label: 'CALL_MANAGEMENT.ACTIONS.PAYMENT_COMMITMENT', value: CallManagementActionType.INBOUND_PAYMENT_COMMITMENT_ACTION_TYPE },
      { label: 'CALL_MANAGEMENT.ACTIONS.PAYMENT_IN_REVIEW', value: CallManagementActionType.INBOUND_PAYMENT_IN_REVIEW_ACTION_TYPE },
      { label: 'CALL_MANAGEMENT.ACTIONS.PAYMENT_RENEGOTIATION', value: CallManagementActionType.INBOUND_PAYMENT_RENEGOTIATION_TYPE },
      { label: 'CALL_MANAGEMENT.ACTIONS.REFINANCE', value: CallManagementActionType.INBOUND_REFINANCE_TYPE },
      { label: 'CALL_MANAGEMENT.ACTIONS.REQUESTED_INFORMATION', value: CallManagementActionType.INBOUND_REQUESTED_INFORMATION_TYPE },
      { label: 'CALL_MANAGEMENT.ACTIONS.CLAIM', value: CallManagementActionType.INBOUND_CLAIM_TYPE },
      { label: 'CALL_MANAGEMENT.ACTIONS.PAYMENT_MADE', value: CallManagementActionType.INBOUND_PAYMENT_MADE_TYPE },
      { label: 'CALL_MANAGEMENT.ACTIONS.RETRY_CALL', value: CallManagementActionType.INBOUND_RETRY_CALL_ACTION_TYPE },
      { label: 'CALL_MANAGEMENT.ACTIONS.PAUSE_COMMUNICATIONS', value: CallManagementActionType.PAUSED_COMMUNICATION_INBOUND_ACTION_TYPE },
      { label: 'CALL_MANAGEMENT.ACTIONS.CONTACT_HUNG_UP', value: CallManagementActionType.INBOUND_CONTACT_HUNG_UP_ACTION_TYPE },

      { label: 'CALL_MANAGEMENT.ACTIONS.PAYMENT_COMMITMENT', value: CallManagementActionType.PAYMENT_COMMITMENT_ACTION_TYPE },
      { label: 'CALL_MANAGEMENT.ACTIONS.PAYMENT_IN_REVIEW', value: CallManagementActionType.PAYMENT_IN_REVIEW_ACTION_TYPE },
      { label: 'CALL_MANAGEMENT.ACTIONS.RETRY_CALL', value: CallManagementActionType.RETRY_CALL_ACTION_TYPE },
      { label: 'CALL_MANAGEMENT.ACTIONS.REFUSE_TO_PAY', value: CallManagementActionType.REFUSE_TO_PAY_ACTION_TYPE },
      { label: 'CALL_MANAGEMENT.ACTIONS.CANT_PAY', value: CallManagementActionType.CANT_PAY_ACTION_TYPE },
      { label: 'CALL_MANAGEMENT.ACTIONS.PAYMENT_RENEGOTIATION', value: CallManagementActionType.PAYMENT_RENEGOTIATION_TYPE },
      { label: 'CALL_MANAGEMENT.ACTIONS.REFINANCE', value: CallManagementActionType.REFINANCE_TYPE },
      { label: 'CALL_MANAGEMENT.ACTIONS.PAYMENT_MADE', value: CallManagementActionType.PAYMENT_MADE_TYPE },
      { label: 'CALL_MANAGEMENT.ACTIONS.COMMUNICATION_REMINDER', value: CallManagementActionType.COMMUNICATION_REMINDER_TYPE },
      { label: 'CALL_MANAGEMENT.ACTIONS.PAUSE_COMMUNICATIONS', value: CallManagementActionType.PAUSED_COMMUNICATION_CONTACT_ACTION_TYPE },
      { label: 'CALL_MANAGEMENT.ACTIONS.CONTACT_HUNG_UP', value: CallManagementActionType.CONTACT_HUNG_UP_ACTION_TYPE },

      { label: 'CALL_MANAGEMENT.ACTIONS.NO_RESPONSE', value: CallManagementActionType.NO_RESPONSE_ACTION_TYPE },
      { label: 'CALL_MANAGEMENT.ACTIONS.WRONG_NUMBER', value: CallManagementActionType.WRONG_PHONE_NUMBER_ACTION_TYPE },
      { label: 'CALL_MANAGEMENT.ACTIONS.MESSAGE_IN_MAILBOX', value: CallManagementActionType.MESSAGE_IN_MAILBOX_TYPE },
      { label: 'CALL_MANAGEMENT.ACTIONS.PAYMENT_MADE', value: CallManagementActionType.PAYMENT_MADE_TYPE },
      { label: 'CALL_MANAGEMENT.ACTIONS.CONTACT_WITH_THIRD_PARTY', value: CallManagementActionType.CONTACT_WITH_THIRD_PARTY_TYPE },
      { label: 'CALL_MANAGEMENT.ACTIONS.PAUSE_COMMUNICATIONS', value: CallManagementActionType.PAUSED_COMMUNICATION_NO_CONTACT_ACTION_TYPE },
      { label: 'CALL_MANAGEMENT.ACTIONS.NO_CONTACTED', value: CallManagementActionType.NO_CONTACTED_ACTION_TYPE }
    ];
  }

  public readonly constantToKeyMapCallManagementActions: { [key: string]: string } = {
    [CallManagementActionType.INBOUND_PAYMENT_COMMITMENT_ACTION_TYPE]: 'inboundPaymentCommitment',
    [CallManagementActionType.INBOUND_PAYMENT_IN_REVIEW_ACTION_TYPE]: 'inboundPaymentInReview',
    [CallManagementActionType.INBOUND_PAYMENT_RENEGOTIATION_TYPE]: 'inboundPaymentRenegotiation',
    [CallManagementActionType.INBOUND_REFINANCE_TYPE]: 'inboundRefinance',
    [CallManagementActionType.INBOUND_REQUESTED_INFORMATION_TYPE]: 'inboundRequestedInformation',
    [CallManagementActionType.INBOUND_CLAIM_TYPE]: 'inboundClaim',
    [CallManagementActionType.INBOUND_PAYMENT_MADE_TYPE]: 'inboundPaymentMade',
    [CallManagementActionType.INBOUND_RETRY_CALL_ACTION_TYPE]: 'inboundRetryCall',
    [CallManagementActionType.PAUSED_COMMUNICATION_INBOUND_ACTION_TYPE]: 'pausedCommunicationInbound',
    [CallManagementActionType.INBOUND_CONTACT_HUNG_UP_ACTION_TYPE]: 'inboundContactHungUp',
    [CallManagementActionType.PAYMENT_COMMITMENT_ACTION_TYPE]: 'paymentCommitment',
    [CallManagementActionType.PAYMENT_IN_REVIEW_ACTION_TYPE]: 'paymentInReview',
    [CallManagementActionType.RETRY_CALL_ACTION_TYPE]: 'retryCall',
    [CallManagementActionType.REFUSE_TO_PAY_ACTION_TYPE]: 'refuseToPay',
    [CallManagementActionType.CANT_PAY_ACTION_TYPE]: 'cantPay',
    [CallManagementActionType.PAYMENT_RENEGOTIATION_TYPE]: 'paymentRenegotiation',
    [CallManagementActionType.REFINANCE_TYPE]: 'refinance',
    [CallManagementActionType.PAYMENT_MADE_TYPE]: 'paymentMade',
    [CallManagementActionType.COMMUNICATION_REMINDER_TYPE]: 'communicationReminder',
    [CallManagementActionType.PAUSED_COMMUNICATION_CONTACT_ACTION_TYPE]: 'pausedCommunicationContact',
    [CallManagementActionType.CONTACT_HUNG_UP_ACTION_TYPE]: 'contactHungUp',
    [CallManagementActionType.NO_RESPONSE_ACTION_TYPE]: 'noResponse',
    [CallManagementActionType.WRONG_PHONE_NUMBER_ACTION_TYPE]: 'wrongPhoneNumber',
    [CallManagementActionType.MESSAGE_IN_MAILBOX_TYPE]: 'messageInMailbox',
    [CallManagementActionType.CONTACT_WITH_THIRD_PARTY_TYPE]: 'contactWithThirdParty',
    [CallManagementActionType.PAUSED_COMMUNICATION_NO_CONTACT_ACTION_TYPE]: 'pausedCommunicationNoContact',
    [CallManagementActionType.NO_CONTACTED_ACTION_TYPE]: 'noContacted',
    [CallManagementActionType.NO_CONVERSATION_ACTION_TYPE]: 'noConversation',

    // FIELD MANAGEMENT
    [FieldManagementActionType.RETRY_MANAGEMENT_ACTION_TYPE]: 'retryManagement',
    [FieldManagementActionType.UPDATE_DATA_ACTION_TYPE]: 'updateData',
    [FieldManagementActionType.NO_ANSWER_ACTION_TYPE]: 'noAnswer',
    [FieldManagementActionType.WRONG_ADDRESS_ACTION_TYPE]: 'wrongAddress',
    [FieldManagementActionType.PAYMENT_IN_REVIEW_OR_COMMITMENT_ACTION_TYPE]: 'paymentCommitment'
  };

  getProcessFilter() : { label: string; value: string }[]{
    return [
      {label : "COMMON.CLIENT_VERIFICATION_PROCESS", value : "CV"}
    ]
  }

  getCommunicationTypesFilter() : { label: string; value: string }[]{
    return [
      {label : "COMMON.COMMUNICATION_TYPES.INBOUND", value : CommunicationType.INBOUND_TYPE},
      {label : "COMMON.COMMUNICATION_TYPES.OUTBOUND", value : CommunicationType.OUTBOUND_TYPE},
    ]
  }

  getVerificationAlertEnumKeyByValue(value: string): string | null {
    const entries = Object.entries(VerificationAlert) as [string, string][];
    for (let [key, enumValue] of entries) {
      if (enumValue === value) {
        return key;
      }
    }
    return null;
  }

  getClaims() {
    return [
      {value : "CONTRACTUAL_CONDITIONS", key : "CLAIMS.FORM.CLAIM.CONTRACTUAL_CONDITIONS"},
      {value : "BILLING_ERRORS", key : "CLAIMS.FORM.CLAIM.BILLING_ERRORS"},
      {value : "PAYMENT_PROBLEM", key : "CLAIMS.FORM.CLAIM.PAYMENT_PROBLEM"},
      {value : "PRODUCT_PROBLEM", key : "CLAIMS.FORM.CLAIM.PRODUCT_PROBLEM"},
      {value : "CLIENT_PROBLEM", key : "CLAIMS.FORM.CLAIM.CLIENT_PROBLEM"},

    ];
  }

  getClientRelationships() {
    return [
      {value : ClientRelationship.GRANDFATHER_OR_GRANDMOTHER, label : "CLIENT.RELATIONSHIP.GRANDFATHER_OR_GRANDMOTHER"},
      {value : ClientRelationship.FRIEND, label : "CLIENT.RELATIONSHIP.FRIEND"},
      {value : ClientRelationship.COWORKER, label : "CLIENT.RELATIONSHIP.COWORKER"},
      {value : ClientRelationship.ACQUAINTANCE, label : "CLIENT.RELATIONSHIP.ACQUAINTANCE"},
      {value : ClientRelationship.SPOUSE_OR_PARTNER, label : "CLIENT.RELATIONSHIP.SPOUSE_OR_PARTNER"},
      {value : ClientRelationship.BROTHER_OR_SISTER_IN_LAW, label : "CLIENT.RELATIONSHIP.BROTHER_OR_SISTER_IN_LAW"},
      {value : ClientRelationship.BROTHER_OR_SISTER, label : "CLIENT.RELATIONSHIP.BROTHER_OR_SISTER"},
      {value : ClientRelationship.SON_OR_DAUGHTER, label : "CLIENT.RELATIONSHIP.SON_OR_DAUGHTER"},
      {value : ClientRelationship.GRANDSON_OR_GRANDDAUGHTER, label : "CLIENT.RELATIONSHIP.GRANDSON_OR_GRANDDAUGHTER"},
      {value : ClientRelationship.FATHER_OR_MOTHER, label : "CLIENT.RELATIONSHIP.FATHER_OR_MOTHER"},
      {value : ClientRelationship.COUSIN, label : "CLIENT.RELATIONSHIP.COUSIN"},
      {value : ClientRelationship.NEPHEW_OR_NIECE, label : "CLIENT.RELATIONSHIP.NEPHEW_OR_NIECE"},
      {value : ClientRelationship.FATHER_OR_OTHER_IN_LAW, label : "CLIENT.RELATIONSHIP.FATHER_OR_OTHER_IN_LAW"},
      {value : ClientRelationship.UNCLE_OR_AUNT, label : "CLIENT.RELATIONSHIP.UNCLE_OR_AUNT"},
      {value : ClientRelationship.SON_OR_DAUGHTER_IN_LAW, label : "CLIENT.RELATIONSHIP.SON_OR_DAUGHTER_IN_LAW"},
      {value : ClientRelationship.OTHER, label : "CLIENT.RELATIONSHIP.OTHER"},

    ];
  }

  getClientRelationshipById(relationship: number) {
    let text = null;
    switch (relationship) {
      case ClientRelationship.GRANDFATHER_OR_GRANDMOTHER:
        text = this._translateService.instant("CLIENT.RELATIONSHIP.GRANDFATHER_OR_GRANDMOTHER");
        break;
      case ClientRelationship.FRIEND:
        text = this._translateService.instant("CLIENT.RELATIONSHIP.FRIEND");
        break;
      case ClientRelationship.COWORKER:
        text = this._translateService.instant("CLIENT.RELATIONSHIP.COWORKER");
        break;
      case ClientRelationship.ACQUAINTANCE:
        text = this._translateService.instant("CLIENT.RELATIONSHIP.ACQUAINTANCE");
        break;
      case ClientRelationship.SPOUSE_OR_PARTNER:
        text = this._translateService.instant("CLIENT.RELATIONSHIP.SPOUSE_OR_PARTNER");
        break;
      case ClientRelationship.BROTHER_OR_SISTER_IN_LAW:
        text = this._translateService.instant("CLIENT.RELATIONSHIP.BROTHER_OR_SISTER_IN_LAW");
        break;
      case ClientRelationship.BROTHER_OR_SISTER:
        text = this._translateService.instant("CLIENT.RELATIONSHIP.BROTHER_OR_SISTER");
        break;
      case ClientRelationship.SON_OR_DAUGHTER:
        text = this._translateService.instant("CLIENT.RELATIONSHIP.SON_OR_DAUGHTER");
        break;
      case ClientRelationship.GRANDSON_OR_GRANDDAUGHTER:
        text = this._translateService.instant("CLIENT.RELATIONSHIP.GRANDSON_OR_GRANDDAUGHTER");
        break;
      case ClientRelationship.FATHER_OR_MOTHER:
        text = this._translateService.instant("CLIENT.RELATIONSHIP.FATHER_OR_MOTHER");
        break;
      case ClientRelationship.COUSIN:
        text = this._translateService.instant("CLIENT.RELATIONSHIP.COUSIN");
        break;
      case ClientRelationship.NEPHEW_OR_NIECE:
        text = this._translateService.instant("CLIENT.RELATIONSHIP.NEPHEW_OR_NIECE");
        break;
      case ClientRelationship.FATHER_OR_OTHER_IN_LAW:
        text = this._translateService.instant("CLIENT.RELATIONSHIP.FATHER_OR_OTHER_IN_LAW");
        break;
      case ClientRelationship.UNCLE_OR_AUNT:
        text = this._translateService.instant("CLIENT.RELATIONSHIP.UNCLE_OR_AUNT");
        break;
      case ClientRelationship.SON_OR_DAUGHTER_IN_LAW:
        text = this._translateService.instant("CLIENT.RELATIONSHIP.SON_OR_DAUGHTER_IN_LAW");
        break;
      case ClientRelationship.OTHER:
        text = this._translateService.instant("CLIENT.RELATIONSHIP.OTHER");
        break;
    }
    return text;
  }

  showAlertSituationIconService(situation:ClientAlertSitutation, additionalData : AdditionalData, showHidden?: boolean):boolean{
    if(!showHidden) showHidden = false;
      const customSituations = additionalData?.customSituations;
    if (!customSituations || !customSituations.length) {
      return false;
    }
    return customSituations.some((e:CustomSituation) => e.situation == situation && (e.hidden ? (showHidden) : true));
  }

  showAlertClaimsIconService(claimData?: ClaimData[]):boolean{
    if (!claimData || !claimData.length) {
      return false;
    }
    return claimData.some((e : ClaimData) => e.claimStatus == ClaimStatus.OPEN);
  }

  replaceDueDateFromCommitmentService(item: any, situation: ClientAlertSitutation = ClientAlertSitutation.PENDING_COMMITMENT): string {
    const obj = item?.additionalData?.customSituations?.find((e: CustomSituation) => e.situation == situation);
    const dueDate = obj?.dueDate != null ? this._datePipe.transform(obj.dueDate, 'dd/MM/yy', 'UTC') : '';
    const isBrokenCommitment = situation === ClientAlertSitutation.BROKEN_COMMITMENT;
    const titleKey = isBrokenCommitment ? 'CLIENT.BROKEN_COMMITMENT' : 'CLIENT.PENDING_COMMITMENT';
    if (isBrokenCommitment && !dueDate) {
      return this._translateService.instant(titleKey);
    }
    const descriptionKey = isBrokenCommitment ? 'CLIENT.BROKEN_COMMITMENT_DESCRIPTION' : 'CLIENT.PENDING_COMMITMENT_DESCRIPTION';
    const description = this._translateService.instant(descriptionKey).replace('%DUE_DATE%', dueDate || '');
    const separator = isBrokenCommitment ? ' - ' : '. ';
    return `${this._translateService.instant(titleKey)}${separator}${description}`;
  }

  geDetailAlertWrongDestinationService(additionalData:AdditionalData){
    let arrayType:any = []
    const customSituations = additionalData?.customSituations ?? [];
    customSituations.forEach((e: CustomSituation) => {
      if (e.situation === ClientAlertSitutation.WRONG_DESTINATION_SITUATION &&!arrayType.includes(e.channelType)) {
        arrayType.push(e.channelType);
      }
    });
    return arrayType;
  }

  getMassiveProcessTemplateLink(projectType: string | null, paymentBalanceType: string, hierarchicalRelation: string | null, economicSectorId: number | null, type: string) {

    const baseUrlCsv = environment.production ? "https://moonflow-public-prd.s3.amazonaws.com/massive-project" : "https://warmi-payment-public-stg.s3.us-east-1.amazonaws.com/massive-project";
    const baseUrlXlsx = environment.production ? "https://moonflow-public-prd.s3.amazonaws.com/massive-process" : "https://warmi-payment-public-stg.s3.us-east-1.amazonaws.com/massive-process";
    const baseUrlXlsxEast = environment.production ? "https://moonflow-public-prd.s3.us-east-1.amazonaws.com/massive-process" : "https://warmi-payment-public-stg.s3.us-east-1.amazonaws.com/massive-process";

    const isEducation = hierarchicalRelation != null &&
                    hierarchicalRelation == HierarchicalRelationEnum.LEVEL_2 &&
                    economicSectorId != null &&
                    economicSectorId == EconomicSectorId.EDUCATION;

    let urlCsv : string | null = baseUrlCsv;
    let urlXlsx = type == MassiveProcessType.PAYMENT_ORDER_TYPE ? baseUrlXlsxEast : baseUrlXlsx;
    switch (type) {
      case MassiveProcessType.CLIENT_PROCESS_TYPE:
        if(projectType == ProjectType.BUSINESS) {
          if(paymentBalanceType == PaymentBalanceType.MONO_INTERFACE_BALANCE_TYPE){
            urlXlsx += "/B2B_Customers_With_Balance.xlsx";
          }else{
            urlCsv += "/b2b/business_customer_template.csv";
            urlXlsx += "/B2B_Customers.xlsx";
          }
        }else {
          if(paymentBalanceType == PaymentBalanceType.MONO_INTERFACE_BALANCE_TYPE){
              urlXlsx += "/B2C_Customers_With_Balance.xlsx";
          } else {
            if (isEducation) {
              urlCsv += "/b2c/client_template_Education.csv";
              urlXlsx += "/B2C_Customers_Education.xlsx";
            } else {
              urlCsv += "/b2c/client_template.csv";
              urlXlsx += "/B2C_Customers.xlsx";
            }
          }
        }
        break;
      case MassiveProcessType.PAYMENT_ORDER_TYPE:
            if(projectType == ProjectType.CLIENTS){
              if(paymentBalanceType == PaymentBalanceType.CALCULATED_TYPE){
                if (isEducation) {
                  urlXlsx += "/B2C%2BPayment%2BOrders%2BEducation.xlsx";
                } else {
                  urlXlsx += "/B2C+Payment+Orders.xlsx";
                }
              } else {
                if (isEducation) {
                  urlXlsx += "/B2C%2BPayment%2BOrders%2B(with%2BBalance)%2BEducation.xlsx";
                } else {
                  urlXlsx += "/B2C+Payment+Orders+(with+Balance).xlsx";
                }
              }
            } else {

              if(paymentBalanceType == PaymentBalanceType.CALCULATED_TYPE) {
                urlXlsx += "/B2B+Payment+Orders.xlsx";
              } else {
                urlXlsx += "/B2B+Payment+Orders+(with+Balance).xlsx";
              }
            }
        break;
      case MassiveProcessType.PAYMENT_ACTION_TYPE:
            if(projectType == ProjectType.CLIENTS){
              if (isEducation) {
                urlXlsx += "/B2C_Payments_Education.xlsx";
              } else {
                urlXlsx += "/B2C_Payments.xlsx";
              }
            } else {
              urlXlsx += "/B2B_Payments.xlsx";
            }
        break;
      case MassiveProcessType.RECOVERY_CAMPAIGN_TYPE:
        if(projectType == ProjectType.CLIENTS){
          if(paymentBalanceType == PaymentBalanceType.CALCULATED_TYPE){
            urlXlsx += "/B2C_Write-off_Portfolio.xlsx";
          } else {
            urlXlsx += "/B2C_Write-off_Portfolio_with_Balance.xlsx";
          }
        } else {
          if(paymentBalanceType == PaymentBalanceType.CALCULATED_TYPE){
            urlXlsx += "/B2B_Write-off_Portfolio.xlsx";
          } else {
            urlXlsx += "/B2B_Write-off_Portfolio_with_Balance.xlsx";
          }
        }
        break;
      case MassiveProcessType.RECOVERY_CAMPAIGN_PAYMENT_ACTION_TYPE:
        if(projectType == ProjectType.CLIENTS){
          urlXlsx += "/B2C_Write-off_Payments.xlsx";
        } else {
          urlXlsx += "/B2B_Write-off_Payments.xlsx";
        }
        break;
    }

    if(urlCsv == baseUrlCsv) urlCsv = null;
    return {urlCsv, urlXlsx};

  }

  formatDurationFromSeconds(seconds: any) {
    if (!seconds || !Number.isFinite(seconds)) return "";

    const sign = seconds < 0 ? "-" : "";
    let s = Math.floor(Math.abs(seconds));

    const h = Math.floor(s / 3600);
    s %= 3600;
    const m = Math.floor(s / 60);
    const sec = s % 60;

    const parts = [];
    if (h > 0) parts.push(`${h}h`);
    if (m > 0) parts.push(`${m}m`);
    if (sec > 0 || parts.length === 0) parts.push(`${sec}s`);

    return sign + parts.join(" ");
  }

  replaceLineBreakWithEmoji(value: string | null | undefined): string {
    if (value == null) return '';
    const lineBreakGlyph = '\u23CE'; // ⏎
    return String(value)
      .replace(/\r\n/g, lineBreakGlyph)
      .replace(/\r/g, lineBreakGlyph)
      .replace(/\n/g, lineBreakGlyph);
  }

  convertStringArrayToArray(value: string, split: string, transformToNumber: boolean) : string[] | number[] {
    if (value) {
      let splitedValues = value.split(split || ",");
      if (!transformToNumber) return splitedValues;
      return splitedValues.map(x => Number(x));
    }
    return [];
  }

  containsNonPaymentOrdersSituation(populationConfiguration: PopulationCriteriaConfiguration[]) {
    if(!populationConfiguration) return false;
    let params : PopulationCriteriaParam[] = populationConfiguration.find(e=> e.id == PopulationCriteriaIdentifier.CLIENT_SITUATION)?.params || [];
    if(!params || !params.length) return false;
    return params?.some(e => {
        if (!e.value) return false;
        const value = this.convertStringArrayToArray(e.value, ",", false) as (string | number)[];
        return value.includes(ClientSitutation.FINALIZED_SITUATION) || value.includes(ClientSitutation.NO_ACTIVE_CREDIT_SITUATION);
    }) ?? false;
  }

  calculateEventDestinationRate(val1:number,val2:number){
    if(val1 == null || val2 == null) return 0;
    if(val2 == 0) return 0;
    if(val1 == 0) return 0;
    return (val2 * 100) / val1;
  }

  getClientContactabilityLevels() {
    return [
      {
        order: 1,
        status: ContactabilityStatusIdentifier.HIGH,
        key: ContactabilityLevel.HIGH,
        color: 'rgba(76,175,80, 1)',
        label: 'STRATEGIES.CONTACTABILITY.LEVEL.HIGH',
        badgeClass: 'info-label-moonflow__success',
        description: 'STRATEGIES.CONTACTABILITY.LEVEL.HIGH_DESCRIPTION',
      },
      {
        order: 2,
        status: ContactabilityStatusIdentifier.MEDIUM,
        key: ContactabilityLevel.MEDIUM,
        color: 'rgba(255,179,0,1)',
        label: 'STRATEGIES.CONTACTABILITY.LEVEL.MEDIUM',
        badgeClass: 'info-label-moonflow__warning',
        description: 'STRATEGIES.CONTACTABILITY.LEVEL.MEDIUM_DESCRIPTION',
      },
      {
        order: 3,
        status: ContactabilityStatusIdentifier.LOW,
        key: ContactabilityLevel.LOW,
        color: 'rgba(251,140,0, 1)',
        label: 'STRATEGIES.CONTACTABILITY.LEVEL.LOW',
        badgeClass: 'info-label-moonflow__low',
        description: 'STRATEGIES.CONTACTABILITY.LEVEL.LOW_DESCRIPTION',
      },
      {
        order: 4,
        status: ContactabilityStatusIdentifier.UNCONTACTABLE,
        key: ContactabilityLevel.UNREACHABLE,
        color: 'rgba(239,68,68,1)',
        label: 'STRATEGIES.CONTACTABILITY.LEVEL.UNREACHABLE',
        badgeClass: 'info-label-moonflow__danger',
        description: 'STRATEGIES.CONTACTABILITY.LEVEL.UNREACHABLE_DESCRIPTION',
      },
      {
        order: 5,
        status: ContactabilityStatusIdentifier.PENDING,
        key: ContactabilityLevel.PENDING,
        color: 'rgba(113,153,183,1)',
        label: 'STRATEGIES.CONTACTABILITY.LEVEL.PENDING',
        badgeClass: 'info-label-moonflow__information',
        description: 'STRATEGIES.CONTACTABILITY.LEVEL.PENDING_DESCRIPTION',
      },
      {
        order: 6,
        status: ContactabilityStatusIdentifier.NO_CONTACTED,
        key: ContactabilityLevel.NOT_CONTACTED,
        color: 'rgba(158,158,158,1)',
        label: 'STRATEGIES.CONTACTABILITY.LEVEL.NOT_CONTACTED',
        badgeClass: 'info-label-moonflow__finished',
        description: 'STRATEGIES.CONTACTABILITY.LEVEL.NOT_CONTACTED_DESCRIPTION',
      },
    ].sort((a, b) => a.order - b.order);
  }

  getPositionFilterByKey(key: string, dataFilter : DropdownFilter[]): DropdownFilter {
    let option = dataFilter.find(e => e.key == key);
    if (option == null || option == undefined) {
      option = new DropdownFilter();
    }
    return option;
  }

  getMoonflowLogoWhite(): string {
    return "./assets/media/logos/moonflow-logo-white.png";
  }

  getMoonflowLogoBlue(): string {
    return "./assets/media/logos/moonflow-logo-blue.png";
  }

  getMoonflowLogoSvg(): string {
    return "./assets/media/logos/logo-2.svg";
  }

  getMoonflowLogoPng(): string {
    return "../../../../../assets/media/img/logo-color-luna-v2.png";
  }

  extraLabelData: {
      [x: number]: string | null;
    } = {
    [AditionalVariableIdentifier.VAR1]: null,
    [AditionalVariableIdentifier.VAR2]: null,
    [AditionalVariableIdentifier.VAR3]: null,
    [AditionalVariableIdentifier.VAR4]: null,
    [AditionalVariableIdentifier.VAR5]: null,
    [AditionalVariableIdentifier.VAR6]: null,
    [AditionalVariableIdentifier.VAR7]: null,
    [AditionalVariableIdentifier.VAR8]: null,
    [AditionalVariableIdentifier.VAR9]: null,
    [AditionalVariableIdentifier.VAR10]: null,
  };

}
